// src/hooks/useUserApi.tsx

import { Auth, Storage } from 'aws-amplify'
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { SensorId } from '@/types/domain'
import { FolderRecord } from '@/types/domain/folder'
import {
  RoleResource,
  UserRecord,
  UserSingle,
  UserPool,
  UserId,
} from '@/types/domain/user'

import {
  authHeader,
  FILTER_TYPE_CONTRACT,
  FILTER_TYPE_FOLDER,
  FILTER_TYPE_SENSOR,
  notifyError,
  notifySuccess,
} from '../api/'
import { usePreloader } from '../components/'
import { languageConstants as LC } from '../constants/'

import { refreshToken } from './auth/'

const { REACT_APP_API_URL } = import.meta.env

const UserCtx = createContext<{
  data: UserPool
  byId: Record<string, UserSingle>
  setData: Dispatch<SetStateAction<UserPool>>
  refresh: (_opts?: { force?: boolean }) => Promise<UserPool>
  getById: (
    _id: number,
    _opts?: { force?: boolean }
  ) => Promise<UserSingle | null>
} | null>(null)

const RoleCtx = createContext<{
  data: RoleResource[]
  setData: Dispatch<SetStateAction<RoleResource[]>>
  refresh: (_opts?: { force?: boolean }) => Promise<RoleResource[]>
} | null>(null)

type Props = {
  children: ReactNode
}

export const UserDataProvider = ({ children }: Props) => {
  const { start, stop } = usePreloader()

  const [users, setUsers] = useState<UserPool>([])
  const [roles, setRoles] = useState<RoleResource[]>([])
  const [byId, setById] = useState<Record<string, UserSingle>>({})

  const usersReqRef = useRef<Promise<UserPool> | null>(null)
  const rolesReqRef = useRef<Promise<RoleResource[]> | null>(null)
  const userReqsRef = useRef<Record<string, Promise<UserSingle | null> | null>>(
    {}
  )

  const withRequest = useCallback(async fn => {
    try {
      await refreshToken()
      return await fn()
    } catch (error) {
      notifyError(`UserApi: ${error?.message}`)
      return null
    }
  }, [])

  const withPreloader = useCallback(
    async fn => {
      start()
      try {
        return await fn()
      } finally {
        stop()
      }
    },
    [start, stop]
  )

  const fetchUsers = useCallback(
    async ({ force = false } = {}): Promise<UserPool> => {
      if (usersReqRef.current && !force) return usersReqRef.current

      usersReqRef.current = (async () => {
        try {
          const data = await withPreloader(() =>
            withRequest(async () => {
              const url = `${REACT_APP_API_URL}/user/admin/users`
              const options = { headers: { ...authHeader() }, method: 'GET' }
              const response = await fetch(url, options)
              if (!response.ok) throw new Error(`GET users ${response.status}`)

              const json = await response.json()
              return json?.data ? json.data : json?.items?.data || []
            })
          )

          if (Array.isArray(data)) setUsers(data)
          return Array.isArray(data) ? data : []
        } finally {
          usersReqRef.current = null
        }
      })()

      return usersReqRef.current
    },
    [withRequest, withPreloader]
  )

  const fetchRoles = useCallback(
    async ({ force = false } = {}): Promise<RoleResource[]> => {
      if (rolesReqRef.current && !force) return rolesReqRef.current

      rolesReqRef.current = (async () => {
        try {
          const data = await withPreloader(() =>
            withRequest(async () => {
              const url = `${REACT_APP_API_URL}/user/admin/user-roles`
              const options = { headers: { ...authHeader() }, method: 'GET' }
              const response = await fetch(url, options)
              if (!response.ok) throw new Error(`GET roles ${response.status}`)
              const json = await response.json()
              return json?.data || []
            })
          )

          if (Array.isArray(data)) setRoles(data)
          return Array.isArray(data) ? data : []
        } finally {
          rolesReqRef.current = null
        }
      })()

      return rolesReqRef.current
    },
    [withRequest, withPreloader]
  )

  const fetchUserById = useCallback(
    async (
      id: UserId,
      { force = false }: { force?: boolean } = {}
    ): Promise<UserSingle | null> => {
      if (!id || Number(id) <= 0) return null
      const key = String(id)
      if (userReqsRef.current[key] && !force) return userReqsRef.current[key]

      userReqsRef.current[key] = (async () => {
        try {
          const data = await withPreloader(() =>
            withRequest(async () => {
              const url = `${REACT_APP_API_URL}/user/admin/users/${id}?include=folders,contracts,sensors`
              const options = { headers: { ...authHeader() }, method: 'GET' }
              const response = await fetch(url, options)
              if (!response.ok) throw new Error(`GET user ${response.status}`)
              const json = await response.json()
              const { data, included } = json || {}
              const attrs = data?.attributes || {}
              const folders = included
                ? included.filter(FILTER_TYPE_FOLDER).map(f => f.attributes)
                : []
              const contracts = included
                ? included.filter(FILTER_TYPE_CONTRACT).map(c => c.attributes)
                : []
              const sensors = included
                ? included.filter(FILTER_TYPE_SENSOR).map(s => Number(s.id))
                : []
              const result = { ...attrs, folders, contracts, sensors }
              setById(prev => ({ ...prev, [key]: result }))
              return result
            })
          )

          return data || null
        } finally {
          userReqsRef.current[key] = null
        }
      })()

      return userReqsRef.current[key]
    },
    [withRequest, withPreloader]
  )

  const didInitUsersRef = useRef(false)
  useEffect(() => {
    if (didInitUsersRef.current) return
    didInitUsersRef.current = true
    fetchUsers()
  }, [fetchUsers])

  const didInitRolesRef = useRef(false)
  useEffect(() => {
    if (didInitRolesRef.current) return
    didInitRolesRef.current = true
    fetchRoles()
  }, [fetchRoles])

  const usersValue = useMemo(
    () => ({
      data: users,
      byId: byId,
      setData: setUsers,
      refresh: fetchUsers,
      getById: fetchUserById,
    }),
    [users, byId, fetchUsers, fetchUserById]
  )

  const rolesValue = useMemo(
    () => ({ data: roles, setData: setRoles, refresh: fetchRoles }),
    [roles, fetchRoles]
  )

  return (
    <UserCtx.Provider value={usersValue}>
      <RoleCtx.Provider value={rolesValue}>{children}</RoleCtx.Provider>
    </UserCtx.Provider>
  )
}

export const useUserPool = () => useContext(UserCtx)?.data ?? []
export const useUserRoles = () => useContext(RoleCtx)?.data ?? []

export const useUser = (id: UserId) => {
  const ctx = useContext(UserCtx)

  const byId = ctx?.byId ?? {}
  const getById = ctx?.getById

  const key = String(id || '')
  const user = key ? byId[key] || null : null

  useEffect(() => {
    if (!id || !getById) return
    if (!user) getById(id)
  }, [id, user, getById])

  return user
}

export const useCoachPool = () => {
  const ctx = useContext(UserCtx)!
  const { data } = ctx

  return useMemo(
    () =>
      data.filter(
        user =>
          user?.attributes?.role_id === 3 && user?.attributes?.is_active === 1
      ),
    [data]
  )
}

export const useUserApi = () => {
  const userCtx = useContext(UserCtx)
  const poolFromCtx = useMemo(() => userCtx?.data ?? [], [userCtx])
  const refreshUsers = userCtx?.refresh

  const withRequest = useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T | null> => {
      try {
        await refreshToken()
        return await fn()
      } catch (error) {
        notifyError(`UserAPI: ${error?.message}`)
        return null
      }
    },
    []
  )

  const getPool = useCallback(async () => {
    if (poolFromCtx.length > 0) return poolFromCtx
    if (!refreshUsers) return []
    const data = await refreshUsers()
    return Array.isArray(data) ? data : []
  }, [poolFromCtx, refreshUsers])

  const uploadToS3 = useCallback(
    async (file: File, dirName: string): Promise<unknown> => {
      const handle = new File(
        [file],
        (
          Math.random().toString(36).substring(2, 16) +
          Math.random().toString(36).substring(2, 16)
        ).toUpperCase() +
          Date.now() +
          file.name.slice(file.name.lastIndexOf('.'))
      )

      const key = `${dirName}/${handle.name}`

      return await Storage.put(key, handle, {
        contentType: handle.type,
      })
    },
    []
  )

  const getFileUpload = useCallback(
    async (file: File, dirName: string = 'users'): Promise<string | null> => {
      try {
        const handle = new File(
          [file],
          (
            Math.random().toString(36).substring(2, 16) +
            Math.random().toString(36).substring(2, 16)
          ).toUpperCase() +
            Date.now() +
            file.name.slice(file.name.lastIndexOf('.'))
        )

        const key = `${dirName}/${handle.name}`

        const result = await Storage.put(key, handle, {
          contentType: handle.type,
        })

        // Public URL (falls dein Bucket public ist)
        return result?.key ? await Storage.get(result.key) : null
      } catch (error) {
        notifyError(error?.message || 'Upload fehlgeschlagen')
        return null
      }
    },
    []
  )

  const recordDataForCreate = useCallback(
    (
      data: Partial<UserRecord> & {
        contracts?: any[]
        folders?: FolderRecord[]
      }
    ) => {
      const {
        city,
        contracts = [],
        country,
        dashboard_is_active,
        email,
        email_contact,
        fax,
        firstname,
        folders = [],
        homepage,
        image_url,
        is_active,
        lastname,
        role_id,
        street,
        telephone,
        telephone_mobile,
        zip,
      } = data
      return {
        data: {
          attributes: {
            city,
            country,
            dashboard_is_active,
            email,
            email_contact,
            fax,
            firstname,
            homepage,
            image_url,
            is_active,
            lastname,
            role_id,
            street,
            telephone,
            telephone_mobile,
            zip,
          },
          relationships: {
            contracts: {
              data: contracts.map(c => ({ type: 'contract', id: c.entity_id })),
            },
            folders: {
              data: folders.map(f => ({ type: 'folder', id: f.entity_id })),
            },
          },
        },
      }
    },
    []
  )

  const recordDataForUpdate = useCallback(data => {
    const {
      city,
      contracts = [],
      country,
      dashboard_is_active,
      email_contact,
      fax,
      firstname,
      folders = [],
      homepage,
      image_url,
      is_active,
      lastname,
      telephone_mobile,
      telephone,
      role_id,
      street,
      zip,
    } = data
    return {
      data: {
        attributes: {
          city,
          country,
          dashboard_is_active,
          email_contact,
          fax,
          firstname,
          homepage,
          image_url,
          is_active,
          lastname,
          role_id,
          street,
          telephone,
          telephone_mobile,
          zip,
        },
        relationships: {
          contracts: {
            data: contracts.map(c => ({ type: 'contract', id: c.entity_id })),
          },
          folders: {
            data: folders.map(f => ({ type: 'folder', id: f.entity_id })),
          },
        },
      },
    }
  }, [])

  const save = useCallback(
    async formData =>
      withRequest(async () => {
        if (!formData?.email || !formData?.password)
          throw new Error('E-Mail und Passwort sind erforderlich')

        const payload = { ...formData }
        if (payload.image_url && typeof payload.image_url === 'object')
          payload.image_url = await getFileUpload(payload.image_url, 'users')

        await Auth.signUp({
          username: payload.email,
          password: payload.password,
          attributes: {
            email: payload.email,
            name: payload.firstname,
            family_name: payload.lastname,
          },
        })

        const url = `${REACT_APP_API_URL}/user/admin/users`
        const options = {
          method: 'POST',
          headers: { ...authHeader(), 'Content-Type': 'application/json' },
          body: JSON.stringify(recordDataForCreate(payload)),
        }
        const response = await fetch(url, options)
        if (!response.ok) throw new Error(`save user: ${response.status}`)

        const json = await response.json()
        await refreshUsers({ force: true })
        return json
      }),
    [recordDataForCreate, refreshUsers, withRequest, getFileUpload]
  )

  const update = useCallback(
    async (id: UserId, formData) =>
      withRequest(async () => {
        if (!id) throw new Error('Benutzer-ID ist erforderlich')

        const payload = { ...formData }
        if (payload.image_url && typeof payload.image_url === 'object')
          payload.image_url = await getFileUpload(payload.image_url, 'users')

        const url = `${REACT_APP_API_URL}/user/admin/users/${id}`
        const options = {
          method: 'PATCH',
          headers: { ...authHeader(), 'Content-Type': 'application/json' },
          body: JSON.stringify(recordDataForUpdate(payload)),
        }

        const response = await fetch(url, options)
        if (!response.ok)
          throw new Error(`update user error ${response.status}`)

        const json = await response.json()
        await refreshUsers({ force: true })
        return json
      }),
    [recordDataForUpdate, refreshUsers, withRequest, getFileUpload]
  )

  const remove = useCallback(
    async (id: UserId, onSuccess) =>
      withRequest(async () => {
        if (!id) throw new Error('Benutzer-ID fehlt')

        const url = `${REACT_APP_API_URL}/user/admin/users/${id}`
        const options = {
          method: 'DELETE',
          headers: { ...authHeader(), 'Content-Type': 'application/json' },
        }

        const response = await fetch(url, options)
        if (!response.ok)
          throw new Error(`delete user error ${response.status}`)

        await refreshUsers({ force: true })
        if (typeof onSuccess === 'function') onSuccess()
        return true
      }),
    [refreshUsers, withRequest]
  )

  const register = useCallback(async (data, onSuccess) => {
    try {
      const { email, password, firstName, lastName } = data || {}
      if (!email || !password)
        throw new Error('E-Mail und Passwort sind erforderlich')

      const res = await Auth.signUp({
        username: email,
        password,
        attributes: {
          email,
          name: firstName,
          family_name: lastName,
        },
      })

      if (typeof onSuccess === 'function') onSuccess(res)
      return { ok: true, data: res }
    } catch (error) {
      notifyError(error?.message || 'Registrierung fehlgeschlagen')
      return { ok: false, error, code: error?.code }
    }
  }, [])

  const resetPassword = useCallback(async (email, password, code) => {
    try {
      if (!email || !password || !code)
        throw new Error('E-Mail, Code und neues Passwort sind erforderlich')

      await Auth.forgotPasswordSubmit(email, code, password)
      notifySuccess(LC.USER_PASSWORD_CHANGED)
      return true
    } catch (error) {
      notifyError(error?.message || 'Fehler beim Zurücksetzen des Passworts')
      return false
    }
  }, [])

  const sendResetCode = useCallback(async email => {
    if (!email) throw new Error('E-Mail ist erforderlich')
    try {
      await Auth.forgotPassword(email)
      notifySuccess(LC.USER_CODE_SENT)
      return true
    } catch (error) {
      notifyError(error)
      return false
    }
  }, [])

  const sendSignup = useCallback(async email => {
    try {
      if (!email) throw new Error('E-Mail ist erforderlich')
      await Auth.resendSignUp(email)
      notifySuccess(LC.USER_MAIL_SENT)
      return true
    } catch (error) {
      notifyError(error?.message || 'Fehler beim Senden der Bestätigung')
      return false
    }
  }, [])

  const getEmailDomains = useCallback(async () => {
    const pool = poolFromCtx.length > 0 ? poolFromCtx : await getPool()
    const emails = pool
      .map(u => u?.attributes?.email)
      .filter(e => typeof e === 'string' && e.includes('@'))
    const domains = emails.map(e => e.substring(e.indexOf('@')))
    return Array.from(new Set(domains)).sort()
  }, [poolFromCtx, getPool])

  const getCoachData = useCallback(
    bridgeId =>
      withRequest(async () => {
        if (!Number(bridgeId)) return null

        const url = `${REACT_APP_API_URL}/sensors/admin/sensors/${bridgeId}?include=contracts`
        const options = { method: 'GET', headers: { ...authHeader() } }
        const response = await fetch(url, options)
        if (!response.ok) throw new Error(`coach data error ${response.status}`)

        const json = await response.json()

        let primaryCoachId = null
        const included = Array.isArray(json?.included) ? json.included : []
        const contractNode =
          included.find(
            n =>
              n?.type === 'contract' ||
              n?.type === 'contracts' ||
              n?.attributes?.primary_coach_id !== null
          ) || null

        if (contractNode?.attributes?.primary_coach_id !== null)
          primaryCoachId = Number(contractNode.attributes.primary_coach_id)

        if (
          !primaryCoachId &&
          json?.data?.attributes?.contracts &&
          json.data.attributes.contracts
        ) {
          primaryCoachId =
            Number(json.data.attributes.contracts[0]?.primary_coach_id) || null
        }

        if (!primaryCoachId) return null

        const pool = await getPool()
        return (pool || []).find(u => Number(u.id) === primaryCoachId) || null
      }),
    [withRequest, getPool]
  )

  const setUsersBySensorId = useCallback(
    (id: SensorId, users) =>
      withRequest(async () => {
        if (!id) throw new Error('SensorId erforderlich')

        const url = `${REACT_APP_API_URL}/sensors/admin/sensors/${id}/users/`
        const options = {
          method: 'PATCH',
          headers: { ...authHeader(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ data: { users } }),
        }
        const response = await fetch(url, options)
        if (!response.ok)
          throw new Error(`sensor user assignment error ${response.status}`)

        const json = await response.json()
        return json || {}
      }),
    [withRequest]
  )

  return {
    getEmailDomains,
    getCoachData,
    uploadToS3,
    getFileUpload,
    save,
    update,
    remove,
    setUsersBySensorId,
    register,
    resetPassword,
    sendResetCode,
    sendSignup,
    refreshUsers,
  }
}
